/**
 * Normalizes upload API responses.
 * Supports legacy string URLs and StorageUploadResult payloads
 * `{ objectKey, fileUrl, signedUrl, fileName }`.
 *
 * Persist {@link #normalizeUploadReference} in the DB.
 * Use {@link #normalizeUploadPreviewUrl} for &lt;img src&gt; / immediate preview only.
 */
export function normalizeUploadReference(data: unknown): string {
  if (!data) return "";
  if (typeof data === "string") {
    const s = data.trim();
    if (!s || s === "[object Object]") return "";
    if (s.startsWith("{") && s.includes("objectKey")) {
      try {
        return normalizeUploadReference(JSON.parse(s));
      } catch {
        /* fall through */
      }
    }
    return s.split(";")[0].split("?")[0];
  }
  if (typeof data === "object") {
    const row = data as Record<string, unknown>;
    const key = row.objectKey ?? row.fileUrl ?? row.url ?? row.s3Url ?? row.key;
    if (typeof key === "string" && key.trim()) {
      return key.split(";")[0].split("?")[0];
    }
  }
  return "";
}

/** Prefer short-lived signed URL for display; fall back to storage key/reference. */
export function normalizeUploadPreviewUrl(data: unknown): string {
  if (!data) return "";
  if (typeof data === "object") {
    const row = data as Record<string, unknown>;
    const signed = row.signedUrl ?? row.previewUrl;
    if (typeof signed === "string" && signed.trim()) {
      return signed.trim();
    }
  }
  return normalizeUploadReference(data);
}

/**
 * Builds a browser-displayable media URL for stored hospital/doctor files.
 * Signed https URLs pass through; private object keys go through the download proxy.
 */
export function resolveStorageDisplayUrl(
  ref?: string | null,
  apiBase: string = process.env.REACT_APP_URL || "",
  opts?: { inline?: boolean },
): string {
  if (!ref) return "";
  const value = String(ref).trim();
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    if (opts?.inline && value.includes("/download") && !value.includes("disposition=")) {
      return `${value}${value.includes("?") ? "&" : "?"}disposition=inline`;
    }
    return value;
  }
  const base = (apiBase || "").replace(/\/+$/, "");
  const path = `/v1/public/upload/download?url=${encodeURIComponent(value)}${
    opts?.inline ? "&disposition=inline" : ""
  }`;
  if (!base) {
    return path;
  }
  return `${base}${path}`;
}
